import React, { useState, useEffect, useRef } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';

// Base URL for API
const API_BASE_URL = 'https://backend.dlapakistan.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

function Education() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [educationData, setEducationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = useRef(null);

  // Fetch education data from backend
  useEffect(() => {
    const fetchEducationData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/education/');
        if (response.data && response.data.heading) {
          setEducationData(response.data);
        } else {
          // No data in backend
          setEducationData(null);
        }
      } catch (error) {
        console.error("Error fetching education data:", error);
        if (error.response?.status === 404) {
          // 404 means no data exists yet - this is normal
          setEducationData(null);
        } else {
          setError("Could not fetch education content. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEducationData();
  }, []);

  // Existing code for header height and sidebar
  useEffect(() => {
    // Header की height पता करें
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        // Fallback height अगर header नहीं मिले
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    // Initial और resize पर update
    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    // Resize observer for dynamic header changes
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const isMobileView = windowWidth <= 1029;

  const sidebarLinks = [
    { text: "Education", border: null, showIcon: false, path: "/about/work-we-do/education" },
    { text: "Regulation", border: null, showIcon: false, path: "/about/work-we-do/regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/about/work-we-do/membership-support" }
  ];

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  // CSS class for mobile overlay
  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen mt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444]"></div>
        <span className="ml-4 text-gray-600">Loading education content...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen mt-20">
        <div className="text-red-500 text-center p-8">
          <p className="text-xl font-semibold mb-2">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // If no data from backend
  if (!educationData) {
    return (
      <div 
        className={`w-full min-h-screen ${mobileOverlayClass}`}
        ref={headerRef}
      >
        
        {/* Main Container */}
        <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
          
          {/* Breadcrumb section */}
          <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
            <span 
              className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
              onClick={() => handleBreadcrumbClick("/")}
            >
              Home
            </span> 
            <span className="mx-2">»</span>
            <span 
              className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
              onClick={() => handleBreadcrumbClick("/about")}
            >
              About The DLA
            </span>
            <span className="mx-2">»</span>
            <span 
              className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
              onClick={() => handleBreadcrumbClick("/about/work-we-do")}
            >
              Work We Do
            </span>
            <span className="mx-2">»</span>
            <span className="text-[#70B444] font-medium">
              Education
            </span>
          </div>

          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
            
            {/* DESKTOP SIDEBAR */}
            {!isMobileView && (
              <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24">
                <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                  <h3 className="m-0 text-base lg:text-lg font-semibold">Work We Do</h3>
                </div>
                {sidebarLinks.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleSidebarClick(item.path)}
                    className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                      item.border ? "border-b border-gray-200" : ""
                    } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                  >
                    {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                    <span className="break-words">{item.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* MAIN CONTENT AREA */}
            <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
              isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
            }`}>
              
              <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                  Education
                </h1>
                
                {isMobileView && (
                  <button
                    onClick={toggleSidebar}
                    className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                  >
                    {isSidebarOpen ? <FaTimes /> : <FaBars />}
                    <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                  </button>
                )}
              </div>

              {/* No Content Message */}
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 14v6l9-5"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 20l-9-5"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-500 mb-2">No Education Content Available</h3>
                <p className="text-gray-400">
                  The education content has not been added yet. Please check back later.
                </p>
              </div>
            </div>

            {/* MOBILE SIDEBAR */}
            {isMobileView && (
              <div
                className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                  isSidebarOpen ? "translate-x-0" : "translate-x-full"
                }`}
                style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
              >
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                    <span className="font-bold text-lg">Navigation</span>
                    <FaTimes 
                      className="cursor-pointer text-2xl" 
                      onClick={toggleSidebar} 
                    />
                  </div>
                  <div className="overflow-y-auto flex-1 p-2">
                    {sidebarLinks.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => handleSidebarClick(item.path)}
                        className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                          location.pathname === item.path 
                            ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                            : "text-gray-700"
                        }`}
                      >
                        {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Overlay */}
            {isMobileView && isSidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
                style={{ top: `${headerHeight}px` }}
                onClick={toggleSidebar}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // If data exists from backend
  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      
      {/* Main Container */}
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section - AboutDla jaisa hi */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/about")}
          >
            About The DLA
          </span>
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/about/work-we-do")}
          >
            Work We Do
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Education
          </span>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          
          {/* DESKTOP SIDEBAR - AboutDla jaisa hi */}
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">Work We Do</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* MAIN CONTENT AREA */}
          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {educationData.heading}
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            {/* Education Content from Backend */}
            <div className="mb-8 md:mb-10 text-gray-700">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: educationData.content }}
              />
            </div>
          </div>

          {/* MOBILE SIDEBAR - AboutDla jaisa hi */}
          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
              style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <FaTimes 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Overlay - AboutDla jaisa hi */}
          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Education;